import classNames from 'owa-classnames';
import { observer } from 'owa-mobx-react';
import { lazyRegisterConditionalFormattingPreview } from 'owa-mail-list-item';
import Draggable from 'owa-dnd/lib/components/Draggable';
import { getDensityMode } from 'owa-fabric-theme';
import { useLazyKeydownHandler } from 'owa-hotkeys';
import VirtualizedLoadMoreListView from 'owa-loadmore-listview/lib/components/VirtualizedLoadMoreListView';
import VirtuosoLoadMoreListView from 'owa-loadmore-listview/lib/components/VirtuosoLoadMoreListView';
import type {
    LoadMoreListViewExtendedVirtualizedProps,
    VirtualizedLoadMoreListViewRef,
} from 'owa-loadmore-listview/lib/components/VirtualizedLoadMoreListView';
import loc from 'owa-localize';
import { getGlobalSettingsAccountMailboxInfo } from 'owa-account-source-list-store';
import { resetFocus, tabIndex } from 'owa-mail-focus-manager';
import { isSingleLineListView, shouldShowListView } from 'owa-mail-layout';
import {
    getMailListGroupHeader,
    getMailListGroupHeaderGenerator,
    addVirtualizedGroupHeadersAction,
    getIndicesOfHiddenRowsInCollapsedGroupHeaders,
    getRowKeysFromCollapsedGroupHeaders,
} from 'owa-virtualized-group-headers';
import {
    mailListItemAnimationStore,
    clearAnimationStore,
    removeCompletedAnimations,
} from 'owa-mail-list-item-animation-store';
import type { MailListTableProps } from 'owa-mail-list-item-shared';
import { addMailListLog, getMailListLogObjectToAddToStore } from 'owa-mail-list-logging';
import { lazyGetHashedLogString } from 'owa-logging-utils/lib/lazyFunctions';
import type { TableView, MailFolderTableQuery } from 'owa-mail-list-store';
import {
    updateStartAndEndIndicesInVLVMutator,
    getFocusedFilterForTable,
    getListViewState,
    getIsEverythingSelectedInTable,
    getItemsOrConversationsSelectedText,
    getSelectedTableView,
    isCatchUpFilter,
    isHeadlinesFilter,
} from 'owa-mail-list-store';
import folderIdToName from 'owa-session-store/lib/utils/folderIdToName';
import getMailListRowDragData from 'owa-mail-list-store/lib/utils/getMailListRowDragData';
import { MessageAdList } from 'owa-mail-messagead-list-view';
import {
    getCanTableLoadMore,
    getCanTableLoadMorePrevious,
    lazyLoadMoreInTable,
} from 'owa-mail-triage-table-load-extra';
import { useComputedValue } from 'owa-react-hooks/lib/useComputed';
import { useMergedRefs } from 'owa-react-hooks/lib/useMergedRefs';
import { useWindowEvent } from 'owa-react-hooks/lib/useWindowEvent';
import type FocusedViewFilter from 'owa-service/lib/contract/FocusedViewFilter';
import type ReactListViewType from 'owa-service/lib/contract/ReactListViewType';
import React from 'react';
import getMailListTableProps from '../utils/getMailListTableProps';
import {
    getMailListDragPreview,
    MAIL_ITEM_DRAG_XOFFSET,
    MAIL_ITEM_DRAG_YOFFSET,
} from '../utils/mailListDragUtil';
import { runListAnimation } from '../utils/runListAnimation';
import { lazySetupMailListContentKeys } from './lazy/lazyListViewModule';
import {
    draggableDiv,
    wrapperDiv,
    outerScrollContainer,
    outerScrollContainerBleedThrough,
    isSingleLineView,
    rowWrapper,
    rowWrapperBleedThrough,
    spinnerPlaceHolder,
} from './MailList.scss';
import { messageListAriaLabel } from './MailListContent.locstring.json';
import MailListGroupHeader from './MailListGroupHeader';
import MailListItemDataProvider from './MailListItemDataProvider';
import MailListLoading from './MailListLoading';
import { assertNever } from 'owa-assert';
import { computed } from 'mobx';
import type MailListItemContextMenuState from 'owa-mail-list-store/lib/store/schema/MailListItemContextMenuState';
import useMailListItemDragEvents from '../hooks/useMailListItemDragEvents';
import { isFeatureEnabled } from 'owa-feature-flags';
import type { ListViewLoadingDirection } from 'owa-mail-loading-action-types';
import { clearItemDataBase64Blob } from 'owa-mail-list-view-pst/lib/utils/itemBase64DataForPST';
import { lazyDragToDownloadEmails } from 'owa-mail-list-view-dnd';
import { MailListItemNoMoreRowsMessageBar } from '../lazyFunctions';
import type { MailboxInfo } from 'owa-client-types';
import {
    lazyFetchShowSettingsHoverAction,
    getStore as getHoverSurfaceActionStore,
} from 'owa-surface-actions-option';

export interface MailListContentProps {
    isFocusOnMailList: () => boolean;
    getIsFocusInMailList: () => boolean;
    listContainer: HTMLDivElement | null;
    tableViewId: string;
    tableView: TableView;
    onScroll?: () => void;
    shouldShowTopMessageAdList: boolean;
    shouldShowFloatingMessageAdList: boolean;
    plannedMiddleMessageAdRow: number;
    styleSelectorAsPerUserSettings: string;
    itemContextMenuState: MailListItemContextMenuState | null;
    isBleedThroughEnabled?: boolean;
}

function useFocusManager(
    props: Pick<
        MailListContentProps,
        'getIsFocusInMailList' | 'isFocusOnMailList' | 'itemContextMenuState'
    >,
    trySetFocus: () => boolean,
    tableView: TableView
) {
    React.useEffect(() => {
        // If the listview loads without cached content, only MailList is displayed.
        // When the mail list content gets rendered later, it needs to transfer focus from MailList to MailListContent
        // to support keyboard navigation.
        /* eslint-disable-next-line no-restricted-properties -- (https://aka.ms/OWALintWiki)
         * BASELINE. DO NOT COPY AND PASTE!
         *	> 'requestAnimationFrame' is restricted from being used. Use safeRequestAnimationFrame in owa-performance instead. */
        window.requestAnimationFrame(() => {
            const isFocusOnMailListComponent =
                document.activeElement === document.getElementById('MailList');
            if (isFocusOnMailListComponent && !props.itemContextMenuState) {
                trySetFocus();
            }
        });
    });

    React.useEffect(() => {
        // Bug 180055 React.useEffect cleanup can't reliably be used to conditionally move focus away from an "about to unmount" focused element
        return () => {
            if (props.getIsFocusInMailList()) {
                // Reset focus such that it can be restored when component is updated
                resetFocus('MailListContentMount');
            }
        };
    }, []);

    const currentShowListPane = shouldShowListView();
    const lastShowListPane = React.useRef<boolean>();
    const lastTableViewId = React.useRef<string>();
    React.useEffect(() => {
        // When list pane changes from shown to hidden, it means we have gone into immersive reading pane and we need to
        // save current scroll position for if user exits and goes back to same list pane (in same table). When user
        // exits immersive reading pane and returns to the same table / previously shown list pane which changes list pane
        // from hidden to shown, we need to reset focus
        if (
            lastShowListPane.current !== undefined &&
            lastShowListPane.current !== currentShowListPane &&
            lastTableViewId.current === tableView.id
        ) {
            resetFocus('MailListContextMenuHideListView');
        }

        lastShowListPane.current = currentShowListPane;
        lastTableViewId.current = tableView.id;
    }, [currentShowListPane]);
}

function useMemoizedMailListTableProps(tableViewId: string) {
    // Because the value depends on computed properties, we can't simply use React.useMemo()
    // with just tableViewId as a dependency. However, we do need to use useMemo to keep the
    // object reference the same
    return React.useMemo(
        () => getMailListTableProps(tableViewId),
        [...Object.values(getMailListTableProps(tableViewId))]
    );
}

const defaultExport: React.MemoExoticComponent<
    React.ForwardRefExoticComponent<
        Omit<MailListContentProps & React.RefAttributes<HTMLDivElement>, 'ref'> &
            React.RefAttributes<HTMLDivElement>
    >
> = observer(
    React.forwardRef(function MailListContent(
        props: MailListContentProps,
        ref: React.Ref<HTMLDivElement>
    ) {
        const {
            tableViewId,
            tableView,
            shouldShowTopMessageAdList,
            shouldShowFloatingMessageAdList,
            styleSelectorAsPerUserSettings,
            isBleedThroughEnabled,
            listContainer,
        } = props;

        const isIconBarGearButtonEnabled = isFeatureEnabled('tri-mlIconBarSettingsButton');
        const showSettingsHoverAction =
            isIconBarGearButtonEnabled && getHoverSurfaceActionStore().showSettingsHoverAction;

        const isRPDenselessEnabled = isFeatureEnabled('rp-denseless');
        const mailListViewContentRef = React.useRef<HTMLDivElement>(null);
        const containerRef = useMergedRefs(mailListViewContentRef, ref);

        const listViewRef = React.useRef<VirtualizedLoadMoreListViewRef>(null);
        const propagateResizeTask = React.useRef<number>();

        const mailListTableProps = useMemoizedMailListTableProps(tableViewId);
        const mailListGroupHeaderGenerator = useComputedValue(
            () => getMailListGroupHeaderGenerator(tableView),
            [tableView]
        );

        // Fetch showSettingsHoverAction once at the list level instead of per icon bar
        React.useEffect(() => {
            if (isIconBarGearButtonEnabled && showSettingsHoverAction === undefined) {
                lazyFetchShowSettingsHoverAction.importAndExecute();
            }
        }, [isIconBarGearButtonEnabled, showSettingsHoverAction]);

        React.useEffect(() => {
            // Register mail conditional formatting preview when MailListContent is mounted
            const mailboxInfo: MailboxInfo = getGlobalSettingsAccountMailboxInfo();
            lazyRegisterConditionalFormattingPreview.importAndExecute(mailboxInfo);
        }, []);

        const trySetFocus = React.useCallback((): boolean => {
            if (mailListViewContentRef.current) {
                mailListViewContentRef.current.focus({ preventScroll: true });

                return true;
            }
            return false;
        }, []);

        let dragToDownloadEmails:
            | ((
                  dataTransfer: DataTransfer,
                  itemIds: string[],
                  subjects: string[],
                  mailboxInfos: MailboxInfo[]
              ) => void)
            | undefined;

        if (!dragToDownloadEmails) {
            lazyDragToDownloadEmails.import().then(module => {
                dragToDownloadEmails = module;
            });
        }

        const [onDragStart, onDragEnd] = useMailListItemDragEvents();

        const onDragStartFunc = React.useCallback(
            (dataTransfer: DataTransfer): void => {
                onDragStart();
                const dragData = getMailListRowDragData();
                if (dragData && dragToDownloadEmails) {
                    dragToDownloadEmails(
                        dataTransfer,
                        dragData.latestItemIds,
                        dragData.subjects,
                        dragData.mailboxInfos
                    );
                }
            },
            [mailListTableProps]
        );

        // Reacts to changes in group headers store as well as tableView properties in the dependency array and recomputes.
        const rowKeysInCollapsedSections = useComputedValue(() => {
            return getRowKeysFromCollapsedGroupHeaders();
        }, []);

        React.useEffect(() => {
            clearItemDataBase64Blob();
            addVirtualizedGroupHeadersAction(tableView, true /* shouldReset */);
        }, [tableView.id]);

        const hiddenRowIndices = React.useMemo((): number[] => {
            return getIndicesOfHiddenRowsInCollapsedGroupHeaders(tableView);
        }, [tableView, rowKeysInCollapsedSections, tableView?.rowPositionUpdatedCount]);

        useLazyKeydownHandler(
            mailListViewContentRef,
            lazySetupMailListContentKeys.importAndExecute,
            tableViewId
        );

        React.useEffect(() => {
            if (mailListItemAnimationStore().pendingAnimations.length > 0) {
                runListAnimation(listViewRef, tableView);
            }
        }, [mailListItemAnimationStore().pendingAnimations, tableView]);

        React.useEffect(() => {
            // Effect body intentionally blank. Here, we return clearAnimationStore as
            // the cleanup side-effect. The returned method is invoked by React when
            // the DependencyList contains changes or the component is unmounted
            // e.g. (folder switch, filter/sort change, search, module switch, etc.)
            return function cleanup() {
                clearAnimationStore();
            };
        }, [tableView.id]);

        // This effect is used to cancel completed animations when the tableView.rowKeys.length changes to reset the
        // position of the completed animations before the rows are re-rendered.
        React.useLayoutEffect(() => {
            if (mailListItemAnimationStore().completedAnimations.length > 0) {
                removeCompletedAnimations();
            }
        }, [tableView.rowKeys.length, tableView.rowPositionUpdatedCount, tableView.reloadCount]);

        // We want to update rowKeysInCollapsedSections for the message list
        // anytime currentLoadedIndex and loadedStartIndex changes because new
        // rows are being added into the list and we should evaluate whether they
        // are displayed or not based on whether they belong to a collapsed or
        // expanded group header.
        React.useEffect(() => {
            addVirtualizedGroupHeadersAction(tableView, false /* shouldReset */);
        }, [
            tableView.id,
            tableView.rowKeys.length,
            tableView.currentLoadedIndex,
            tableView.loadedStartIndex,
            tableView.reloadCount,
            tableView.rowPositionUpdatedCount,
        ]);

        // Propagate resize task to avoid calling too many times
        useWindowEvent(
            'resize',
            () => {
                if (propagateResizeTask.current) {
                    window.clearTimeout(propagateResizeTask.current);
                    propagateResizeTask.current = undefined;
                }

                propagateResizeTask.current = window.setTimeout(() => {
                    propagateResizeTask.current = undefined;
                }, 150);
            },
            'MailListContent',
            []
        );

        useFocusManager(props, trySetFocus, tableView);

        const getCanCurrentTableLoadMore = React.useCallback((): boolean => {
            return mailListItemAnimationStore().activeAnimations.length > 0 ||
                mailListItemAnimationStore().pendingAnimations.length > 0 // When animating we dont want to load more rows because it can effect the position of other items and the layout.
                ? false
                : getCanTableLoadMore(tableView);
        }, [
            tableView,
            mailListItemAnimationStore().activeAnimations,
            mailListItemAnimationStore().pendingAnimations,
        ]);

        const getCanCurrentTableLoadMorePrevious = React.useCallback((): boolean => {
            return mailListItemAnimationStore().activeAnimations.length > 0 ||
                mailListItemAnimationStore().pendingAnimations.length > 0 // When animating we dont want to load more rows because it can effect the position of other items and the layout.
                ? false
                : getCanTableLoadMorePrevious(tableView);
        }, [
            tableView,
            mailListItemAnimationStore().activeAnimations,
            mailListItemAnimationStore().pendingAnimations,
        ]);

        const getAriaLabel = () => {
            let ariaLabel = loc(messageListAriaLabel);

            if (getIsEverythingSelectedInTable(tableView) || tableView.selectedRowKeys.size == 0) {
                ariaLabel += ' ' + getItemsOrConversationsSelectedText(tableViewId);
            }

            return ariaLabel;
        };

        /**
         * Clear tabindex of container such that container is not in a sequential
         * keyboard navigation when an item is selected in listview
         */
        const resetListViewContentTabIndex = React.useCallback(() => {
            const mailListViewContent = mailListViewContentRef.current;
            if (mailListViewContent?.getAttribute('tabindex') === tabIndex.sequentialIndex) {
                mailListViewContent.setAttribute('tabindex', tabIndex.nonSequentialIndex);
            }
        }, []);

        const onLoadMoreRows = React.useCallback(
            (loadingDirection: ListViewLoadingDirection) => {
                if (!tableView.isLoading) {
                    lazyLoadMoreInTable.importAndExecute(tableView, loadingDirection, listViewRef);
                }
            },
            [tableView]
        );

        const canDrag = React.useCallback(() => {
            return mailListTableProps.canDragFromTable;
        }, [mailListTableProps.canDragFromTable]);

        const isDraggable = React.useCallback(() => {
            const dragData = getMailListRowDragData();
            return !!dragData && dragData.rowKeys.length > 0 && mailListTableProps.isDraggable;
        }, [mailListTableProps.isDraggable]);

        const isScrollPerfEnabled = React.useMemo(() => isFeatureEnabled('fwk-scroll-perf'), []);
        const createMailListItem = React.useCallback(
            (
                rowKey: string,
                _itemIndex: number,
                listProps: MailListTableProps,
                // this will skip rendering of things that don't need to be there if it will only be on the screen for a short time
                // such as event listeners and hover effects
                renderMinimum: boolean
            ) => {
                // The unique key must be provided to the outermost element, otherwise React will
                // default to using the list index. This would be VERY bad as any row delete/additions
                // will cause a re-render
                if (
                    tableView.rowKeys.includes(rowKey) &&
                    !rowKeysInCollapsedSections.includes(rowKey)
                ) {
                    return (
                        <React.Fragment key={`item_${rowKey}`}>
                            <MailListItemDataProvider
                                rowKey={rowKey}
                                listRef={listContainer}
                                mailListTableProps={listProps}
                                resetListViewContentTabIndex={resetListViewContentTabIndex}
                                getIsFocusInMailList={props.getIsFocusInMailList}
                                styleSelectorAsPerUserSettings={styleSelectorAsPerUserSettings}
                                renderMinimum={renderMinimum && isScrollPerfEnabled}
                            />
                        </React.Fragment>
                    );
                } else {
                    return null;
                }
            },
            [
                tableView.id,
                tableView.rowKeys.length,
                resetListViewContentTabIndex,
                props.getIsFocusInMailList,
                styleSelectorAsPerUserSettings,
                rowKeysInCollapsedSections.length,
                listContainer,
            ]
        );

        const createHeader = React.useCallback(
            (previousRowKey: string | null, currentRowKey: string) => {
                const headers: JSX.Element[] = [];
                const headerText = getMailListGroupHeader(
                    previousRowKey,
                    currentRowKey,
                    tableView,
                    mailListGroupHeaderGenerator
                );

                if (
                    headerText &&
                    !isCatchUpFilter(tableView?.tableQuery as MailFolderTableQuery) &&
                    !isHeadlinesFilter(tableView?.tableQuery as MailFolderTableQuery)
                ) {
                    headers.push(
                        <MailListGroupHeader
                            key={`gh_${headerText}`}
                            headerText={headerText}
                            groupHeaderStylesAsPerUserSettings={styleSelectorAsPerUserSettings}
                            tableView={tableView}
                            onCollapse={onCollapse}
                        />
                    );

                    lazyGetHashedLogString.import().then(getHashedLogString => {
                        addMailListLog(
                            getMailListLogObjectToAddToStore('GroupHeaderAddedToListView', {
                                tableViewId: tableView.id,
                                headerText: getHashedLogString(headerText),
                                previousRowKey,
                                currentRowKey,
                            })
                        );
                    });
                }

                if (headers.length === 0) {
                    return null;
                }

                return <React.Fragment key={`header_${currentRowKey}`}>{headers}</React.Fragment>;
            },
            [mailListGroupHeaderGenerator, tableView, styleSelectorAsPerUserSettings]
        );

        const loadingSpinner = React.useMemo((): JSX.Element => {
            return <MailListLoading />;
        }, []);

        const focusedRowKeyIndex = React.useMemo((): number => {
            const focusedRowKey = tableView.focusedRowKey;
            return focusedRowKey ? tableView.rowKeys.indexOf(focusedRowKey) : -1;
        }, [tableView.focusedRowKey, tableView.rowKeys]);

        const getDragData = React.useCallback(() => {
            const mailListRowDragData = getMailListRowDragData();
            if (mailListRowDragData) {
                return mailListRowDragData;
            }
            return {
                itemType: null,
            };
        }, []);

        const subComponentProps: MailVirtualizedSubComponentProps = React.useMemo(
            () => ({
                tableView,
                shouldShowTopMessageAdList,
                shouldShowFloatingMessageAdList,
                styleSelectorAsPerUserSettings,
                getCanCurrentTableLoadMore,
            }),
            [
                tableView,
                shouldShowTopMessageAdList,
                shouldShowFloatingMessageAdList,
                styleSelectorAsPerUserSettings,
                getCanCurrentTableLoadMore,
            ]
        );

        const renderListViewContent = (): JSX.Element => {
            const listProps: LoadMoreListViewExtendedVirtualizedProps = {
                estimatedRowHeight: getEstimatedRowHeight(),
                updateStartAndEndIndices: updateStartAndEndIndicesInVLVMutator,
                activeAnimationsCount: mailListItemAnimationStore().activeAnimations.length,
                hiddenRowIndices,
            };
            const loadMoreListView = (
                <div role="region" className={wrapperDiv}>
                    <div
                        tabIndex={0}
                        role="listbox"
                        aria-multiselectable={true}
                        aria-label={getAriaLabel()}
                        ref={containerRef}
                        className={wrapperDiv}
                        aria-activedescendant={tableView.focusedRowKey ?? undefined}
                    >
                        {isFeatureEnabled('tri-virtuoso') ? (
                            <VirtuosoLoadMoreListView
                                ref={listViewRef}
                                className={classNames(
                                    'customScrollBar',
                                    outerScrollContainer,
                                    isRPDenselessEnabled &&
                                        isBleedThroughEnabled &&
                                        outerScrollContainerBleedThrough,
                                    // If the list is single line, we apply the single
                                    isSingleLineListView() && isSingleLineView
                                )}
                                dataSourceId={tableView.id}
                                itemIds={tableView.rowKeys}
                                focusedRowKeyIndex={focusedRowKeyIndex}
                                focusedRowKey={tableView.focusedRowKey}
                                focusedNodeId={
                                    getListViewState().expandedConversationViewState.focusedNodeId
                                }
                                onScroll={props.onScroll}
                                onRenderRow={createMailListItem}
                                onRenderHeader={createHeader}
                                onLoadMoreRows={onLoadMoreRows}
                                isLoadRowsInProgress={tableView.isLoading}
                                getCanLoadMore={getCanCurrentTableLoadMore}
                                getCanLoadMorePrevious={getCanCurrentTableLoadMorePrevious}
                                currentLoadedIndex={tableView.currentLoadedIndex}
                                loadedStartIndex={tableView.loadedStartIndex}
                                loadingComponent={loadingSpinner}
                                rowWrapperClass={classNames(
                                    rowWrapper,
                                    isRPDenselessEnabled && rowWrapperBleedThrough
                                )}
                                PreRowsComponent={PreRows}
                                MidRowsComponent={MidRows}
                                midRowsComponentRowNumber={props.plannedMiddleMessageAdRow}
                                PostRowsComponent={PostRows}
                                listProps={mailListTableProps}
                                subComponentProps={subComponentProps}
                                {...listProps}
                            />
                        ) : (
                            <VirtualizedLoadMoreListView
                                ref={listViewRef}
                                className={classNames(
                                    'customScrollBar',
                                    outerScrollContainer,
                                    isRPDenselessEnabled && outerScrollContainerBleedThrough,
                                    isSingleLineListView() && isSingleLineView
                                )}
                                dataSourceId={tableView.id}
                                itemIds={tableView.rowKeys}
                                focusedRowKeyIndex={focusedRowKeyIndex}
                                focusedRowKey={tableView.focusedRowKey}
                                focusedNodeId={
                                    getListViewState().expandedConversationViewState.focusedNodeId
                                }
                                onScroll={props.onScroll}
                                onRenderRow={createMailListItem}
                                onRenderHeader={createHeader}
                                onLoadMoreRows={onLoadMoreRows}
                                isLoadRowsInProgress={tableView.isLoading}
                                getCanLoadMore={getCanCurrentTableLoadMore}
                                getCanLoadMorePrevious={getCanCurrentTableLoadMorePrevious}
                                currentLoadedIndex={tableView.currentLoadedIndex}
                                loadedStartIndex={tableView.loadedStartIndex}
                                loadingComponent={loadingSpinner}
                                rowWrapperClass={classNames(
                                    rowWrapper,
                                    isRPDenselessEnabled && rowWrapperBleedThrough
                                )}
                                PreRowsComponent={PreRows}
                                MidRowsComponent={MidRows}
                                midRowsComponentRowNumber={props.plannedMiddleMessageAdRow}
                                PostRowsComponent={PostRows}
                                listProps={mailListTableProps}
                                subComponentProps={subComponentProps}
                                {...listProps}
                            />
                        )}
                    </div>
                </div>
            );

            return loadMoreListView;
        };

        return (
            <Draggable
                canDrag={canDrag}
                isDraggable={isDraggable}
                onDragStart={onDragStartFunc}
                onDragEnd={onDragEnd}
                getDragData={getDragData}
                getDragPreview={getMailListDragPreview}
                xOffset={MAIL_ITEM_DRAG_XOFFSET}
                yOffset={MAIL_ITEM_DRAG_YOFFSET}
                classNames={draggableDiv}
            >
                {renderListViewContent()}
            </Draggable>
        );
    }),
    'MailListContent'
);
export default defaultExport;

interface MailVirtualizedSubComponentProps {
    tableView: TableView;
    shouldShowTopMessageAdList: boolean;
    shouldShowFloatingMessageAdList: boolean;
    styleSelectorAsPerUserSettings: string;
    getCanCurrentTableLoadMore: () => boolean;
}

const PreRows = observer(function PreRows({
    tableView,
    shouldShowTopMessageAdList,
    shouldShowFloatingMessageAdList,
}: MailVirtualizedSubComponentProps) {
    return (
        <>
            {shouldShowTopMessageAdList && (
                <MessageAdList
                    loadInFocusedPivot={getFocusedFilterForTable(tableView) == 0}
                    loadInOtherPivot={getFocusedFilterForTable(tableView) == 1}
                    loadInEmptyPivot={false}
                    loadInItemView={tableView.tableQuery.listViewType == 1}
                    loadOnHeader={false}
                    requestingAnotherFloatingAd={shouldShowFloatingMessageAdList}
                    middleFloatingAdOnly={false}
                    shouldShowTwoAds={
                        getFocusedFilterForTable(tableView) == 1 && !shouldShowFloatingMessageAdList
                    }
                    tableView={tableView}
                    adIndex={0}
                />
            )}
        </>
    );
},
'PreRows');

const MidRows = observer(function MidRows({
    tableView,
    shouldShowTopMessageAdList,
    shouldShowFloatingMessageAdList,
}: MailVirtualizedSubComponentProps) {
    return (
        <>
            {shouldShowFloatingMessageAdList && (
                <MessageAdList
                    loadInFocusedPivot={getFocusedFilterForTable(tableView) == 0}
                    loadInOtherPivot={getFocusedFilterForTable(tableView) == 1}
                    loadInEmptyPivot={false}
                    loadInItemView={tableView.tableQuery.listViewType == 1}
                    loadOnHeader={false}
                    requestingAnotherFloatingAd={shouldShowTopMessageAdList}
                    middleFloatingAdOnly={!shouldShowTopMessageAdList}
                    shouldShowTwoAds={false}
                    tableView={tableView}
                    adIndex={shouldShowTopMessageAdList ? 1 : 0}
                />
            )}
        </>
    );
},
'MidRows');

const PostRows = observer(function PostRows({
    tableView,
    getCanCurrentTableLoadMore,
}: MailVirtualizedSubComponentProps) {
    // Do not show the placeholder if we're loading
    if (tableView.isLoading) {
        return null;
    }

    const canLoadMore = getCanCurrentTableLoadMore();

    // If the table can load more, we show a spinner placeholder to indicate that more rows can be loaded
    // and the user can scroll down to load more rows.
    // If it can't load more we will show MailListItemNoMoreRowsMessageBar component which will have its own decision on whether to show a message or not.
    return canLoadMore ? (
        <div className={spinnerPlaceHolder} />
    ) : (
        <MailListItemNoMoreRowsMessageBar tableView={tableView} />
    );
},
'PostRows');

const computedEstimatedRowHeight = computed((): number => {
    const densityMode = getDensityMode();
    const isJunkFolder =
        folderIdToName(getSelectedTableView()?.tableQuery?.folderId ?? '') === 'junkemail';

    if (isSingleLineListView()) {
        switch (densityMode) {
            case 'Full':
                return 36.796875;
            case 'Simple':
                return 32.796875;
            case 'Compact':
                return 25;
            default:
                assertNever(densityMode);
        }
    } else {
        switch (densityMode) {
            case 'Full':
                return isJunkFolder ? 100 : 80.8;
            case 'Simple':
                return isJunkFolder ? 92 : 72.796875;
            case 'Compact':
                return isJunkFolder ? 79 : 62.796875;
            default:
                assertNever(densityMode);
        }
    }
});

const getEstimatedRowHeight = () => computedEstimatedRowHeight.get();
const onCollapse = () => {
    // no-op function to be passed to MailListGroupHeader to avoid passing a new function on each render
};
